import { XMarkIcon } from '@heroicons/react/24/outline';
import {
	GQLDesignPageFragment,
	GQLMerchTileFragment,
	GQLOrderItemFragment,
	GQLProductFragment,
	GQLProductSideSideType,
} from '@theprintbar/graphql';
import { Merch, cn, useDebounce } from '@theprintbar/shared';
import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { PrimaryButton } from '../../Button';
import type { CreativeEditorInstance } from '../../CreativeEditor';
import { editorTools } from '../../CreativeEditor/editorTools';
import { updateBackgroundImages } from '../../CreativeEditor/utils/updateImages';
import { Spinner } from '../../Spinner';
import { UploadDisclaimer } from './UploadDisclaimer';
import { ArtworkInfoPanel } from './artworkInfoPanel';
import { handleEditorCreateScene } from './handleEditorCreateScene';
import { handleEditorUpdate } from './handleEditorUpdate';

interface DesignerProps {
	editor: CreativeEditorInstance;
	licence: string;
	domain: string;
	assetsUrl: string;
	cesdkAssetsUrl: string;
	content: GQLDesignPageFragment;
	product: GQLProductFragment;
	merch?: Merch;
	orderItem?: GQLOrderItemFragment | null;
	merchItem?: GQLMerchTileFragment | null;
	designHandlerUrl: string;
	activeSide: GQLProductSideSideType;
	setUpdating: (updating: boolean) => void;
	updateMerch: (newMerch: Merch, firstUpdate: boolean) => void;
	setEditor: (editor: CreativeEditorInstance) => void;
	onError?: (error: string) => void;
	openTutorial?: () => void;
	mobileOpen: boolean;
	setMobileOpen: (open: boolean) => void;
	children?: React.ReactNode;
	details?: Array<{ title: string; content: string }>;
	isPod?: boolean;
	crew?: boolean;
}

const CreativeEditor = lazy(() => import('../../CreativeEditor').then(m => ({ default: m.CreativeEditor })));

export function MerchDesigner({
	editor,
	licence,
	domain,
	assetsUrl,
	cesdkAssetsUrl,
	product,
	merch,
	content,
	orderItem,
	merchItem,
	designHandlerUrl,
	activeSide,
	children,
	details,
	isPod,
	crew,
	setUpdating,
	updateMerch,
	setEditor,
	onError,
	openTutorial,
	mobileOpen,
	setMobileOpen,
}: DesignerProps) {
	const [loading, setLoading] = useState(true);
	const [lastColour, setLastColour] = useState(merch?.colour);
	const [lastSide, setLastSide] = useState(activeSide);
	const [editorUpdated, setUpdated] = useState(false);
	const [triggerUpdate, setTriggerUpdate] = useState(false);
	const [hasUploaded, setHasUploaded] = useState(false);
	const [initialUpdate, setInitialUpdate] = useState(false);
	const [firstUpdateComplete, setFirstUpdateComplete] = useState(false);
	let [debouncedFirstUpdate] = useDebounce(initialUpdate, 1000);
	let [debouncedUpdate] = useDebounce(triggerUpdate, 200);

	const createScene = useCallback(
		async (e: CreativeEditorInstance) => {
			if (!e?.engine?.editor) {
				console.log('No editor returned on createScene');
				return;
			}

			//Wait for the page to be visible before creating the scene
			while (document.visibilityState !== 'visible') {
				await new Promise(resolve => setTimeout(resolve, 500));
			}

			await handleEditorCreateScene({
				editor: e,
				product,
				merch,
				merchItem,
				domain,
				orderItem,
				content,
				updateMerch: merch => updateMerch(merch, true),
			});
			setInitialUpdate(true);
			// Skip 'Activated' (history-stack switch) — only real edits mark the design as updated.
			e.engine.editor.onHistoryUpdatedWithKind(kind => {
				if (kind === 'Updated') setUpdated(true);
			});
		},
		[merch, orderItem, product, domain],
	);

	//Debounc the initial update trigger
	useEffect(() => {
		if (debouncedFirstUpdate) {
			setUpdated(true);
		}
	}, [debouncedFirstUpdate]);

	//Debounc the searchInput
	useEffect(() => {
		if (debouncedUpdate) {
			setUpdating(true);
		}
	}, [debouncedUpdate]);

	const handleUpdate = useCallback(async () => {
		if (!merch) {
			console.log('No merch found');
			return;
		} else if (!editor) {
			console.log('No editor found');
			return;
		}
		setTriggerUpdate(true);
		try {
			const newMerch = await handleEditorUpdate({
				editor,
				product,
				domain,
				activeSide,
				currentMerch: merch,
				firstUpdate: !firstUpdateComplete,
			});
			updateMerch(newMerch, !firstUpdateComplete);
		} catch (e) {
			console.error(e);
			onError?.('Something went wrong, please refresh and try again');
		}
		setFirstUpdateComplete(true);
		setTriggerUpdate(false);
		setUpdating(false);
		setUpdated(false);
		setLoading(false);
	}, [editorUpdated, editor, product, activeSide, merch, firstUpdateComplete, updateMerch]);

	useEffect(() => {
		if (editorUpdated && debouncedFirstUpdate) {
			handleUpdate();
		}
	}, [editorUpdated, debouncedFirstUpdate]);

	useEffect(() => {
		if (editor && merch?.colour !== lastColour) {
			if (!merch) return;
			updateBackgroundImages(editor, product, merch.colour, domain);
			setLastColour(merch.colour);
		}
	}, [merch, editor, lastColour]);

	useEffect(() => {
		if (editor && activeSide !== lastSide) {
			setLoading(true);
			setTimeout(() => {
				editorTools.switchToSide(editor, activeSide);
				setLastSide(activeSide);
				setTimeout(() => setLoading(false), 600);
			}, 100);
		}
	}, [editor, activeSide]);

	const CEditor = useMemo(
		() =>
			merch ? (
				<Suspense fallback={<div />}>
					<CreativeEditor
						isPod={isPod}
						crew={crew}
						WEB_DOMAIN={domain}
						assetsUrl={assetsUrl}
						cesdkAssetsUrl={cesdkAssetsUrl}
						licence={licence}
						designHandlerUrl={designHandlerUrl}
						createScene={createScene}
						onLoad={e => setEditor(e)}
						onUpload={() => {
							setHasUploaded(true);
						}}
						openTutorial={openTutorial}
						className="w-full h-full"
						content={content}
					/>
				</Suspense>
			) : null,
		[domain, licence, merch, designHandlerUrl, createScene],
	);

	const editorPanel = (
		<>
			<div className="sticky top-0 flex-1 overflow-hidden bg-white lg:rounded-3xl lg:border lg:border-blue">
				{CEditor}
				<ArtworkInfoPanel product={product} mobileView={crew || isPod} merch={merch} activeSide={activeSide} editor={editor} />
				<div
					className={cn(
						'transition-all z-[999] w-full h-full bg-white flex items-center justify-center absolute inset-0 opacity-0 pointer-events-none',
						loading && 'opacity-100',
					)}
				>
					<Spinner className="h-8 w-8 text-grey mr-2" />
					<div>
						<h5 className="text-blue text-sm">Loading Editor</h5>
						<p className="text-xs">Just a few seconds...</p>
					</div>
				</div>
			</div>
			{details && (
				<div className="hidden lg:flex items-center justify-center space-x-2 mt-4">
					{details.map(({ title, content }) => (
						<div key={title} className="flex flex-col gap-2 items-center min-w-16">
							<h5 className="uppercase text-xs">{title}</h5>
							<p className="text-xs">{content}</p>
						</div>
					))}
				</div>
			)}
			{children}
		</>
	);

	useEffect(() => {
		setTimeout(() => {
			if (mobileOpen) {
				document.body.style.overflow = 'hidden';
			} else {
				document.body.style.overflow = '';
			}
		}, 300);
		// Cleanup function to revert overflow when component unmounts or mobileOpen changes
		return () => {
			setTimeout(() => {
				document.body.style.overflow = '';
			}, 300);
		};
	}, [mobileOpen]);

	return (
		<>
			<UploadDisclaimer hasUploaded={hasUploaded} content={content} />
			<div
				className={cn(
					'flex flex-col lg:col-span-5 z-[9999] lg:z-0 fixed lg:static lg:max-h-[880px]',
					'inset-0 transition duration-300 ease-out',
					mobileOpen ? 'translate-y-0' : 'translate-y-full lg:translate-y-0',
				)}
			>
				<div className="lg:hidden bg-white-pearl px-4 py-2">
					<div className="flex items-center justify-between">
						<h4 className="text-lg font-semibold">Design Your Merch</h4>
						<PrimaryButton type="button" icon={XMarkIcon} onClick={() => setMobileOpen(false)}>
							Save and close
						</PrimaryButton>
					</div>
				</div>
				{editorPanel}
			</div>
		</>
	);
}
